import React from "react";
import { Modal as MuiModal, Backdrop, Fade } from "@mui/material";

import SoftBox from "../../../components/SoftBox";
import "./customize.css";

/**
 * The one confirm for anything that replaces the whole board.
 *
 * Two actions used to wipe a board silently: applying a template ("Applying a
 * template replaces the current draft directly — no confirm step") and Reset to
 * default. Both now come through here, so the wording and the weight of the
 * decision are identical, and both name exactly what is about to be lost —
 * a count is not enough when the answer to "what will I lose?" is the whole
 * reason you are hesitating.
 *
 * It never appears when there is nothing to lose: an empty board applies
 * straight through. A confirm on a no-op only teaches people to click past it.
 *
 * Built on the SAME frosted shell as TemplatePicker / CustomizeViewModal.
 */
const FONT = `"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif`;
const HAIRLINE = "rgba(15,23,42,0.10)";

const SHELL = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: { mini: "92%", sm: "460px" },
  background: "linear-gradient(180deg, rgba(255,255,255,0.90) 0%, rgba(255,255,255,0.86) 100%)",
  boxShadow: "0px 24px 48px -8px rgba(16,24,40,0.20), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
  borderRadius: "8px",
  backdropFilter: "blur(30px) saturate(185%)",
  WebkitBackdropFilter: "blur(30px) saturate(185%)",
  border: "1px solid rgba(255,255,255,0.6)",
  overflow: "hidden",
  fontFamily: FONT,
};

const ConfirmWipe = ({ open, what, losing = [], confirmLabel = "Replace board", onCancel, onConfirm, brandVars }) => (
  <MuiModal
    open={!!open}
    onClose={onCancel}
    BackdropComponent={Backdrop}
    BackdropProps={{
      timeout: 240,
      sx: {
        backgroundColor: "rgba(15,23,42,0.16)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
      },
    }}
    closeAfterTransition
  >
    <Fade in={!!open}>
      <SoftBox sx={SHELL} style={brandVars}>
        <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "20px 22px 16px" }}>
          <SoftBox
            sx={{
              width: "34px",
              height: "34px",
              flexShrink: 0,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              background: "#FFFAEB",
              color: "#B54708",
            }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
          </SoftBox>
          <SoftBox sx={{ minWidth: 0 }}>
            <SoftBox sx={{ fontFamily: FONT, fontSize: "15px", fontWeight: 500, color: "#0F172A" }}>
              Replace your board?
            </SoftBox>
            <SoftBox sx={{ marginTop: "4px", fontFamily: FONT, fontSize: "12.5px", lineHeight: 1.55, color: "#667085" }}>
              {what} replaces everything on this dashboard. Your arrangement and any titles or scenes you changed
              are dropped.
            </SoftBox>
            {losing.length > 0 && (
              <>
                <SoftBox sx={{ marginTop: "12px", fontFamily: FONT, fontSize: "11px", fontWeight: 600, color: "#98A2B3" }}>
                  {losing.length} component{losing.length === 1 ? "" : "s"} will be removed
                </SoftBox>
                <SoftBox sx={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "7px" }}>
                  {losing.map((n) => (
                    <SoftBox
                      key={n}
                      sx={{
                        fontFamily: FONT,
                        fontSize: "10.5px",
                        color: "#93370D",
                        background: "#FFFAEB",
                        border: "1px solid #FEDF89",
                        borderRadius: "999px",
                        padding: "2px 9px",
                      }}
                    >
                      {n}
                    </SoftBox>
                  ))}
                </SoftBox>
              </>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            padding: "14px 22px 18px",
            borderTop: `1px solid ${HAIRLINE}`,
            background: "rgba(15,23,42,0.03)",
          }}
        >
          <SoftBox
            component="button"
            type="button"
            onClick={onCancel}
            sx={{
              height: "40px",
              padding: "0 16px",
              background: "transparent",
              border: `1px solid ${HAIRLINE}`,
              borderRadius: "8px",
              color: "#0F172A",
              fontFamily: FONT,
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": { background: "rgba(15,23,42,0.04)" },
            }}
          >
            Cancel
          </SoftBox>
          <SoftBox
            component="button"
            type="button"
            onClick={onConfirm}
            sx={{
              height: "40px",
              padding: "0 18px",
              border: 0,
              borderRadius: "8px",
              cursor: "pointer",
              background: "#B42318",
              color: "#fff",
              fontFamily: FONT,
              fontSize: "13px",
              fontWeight: 500,
              "&:hover": { background: "#912018" },
            }}
          >
            {confirmLabel}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </Fade>
  </MuiModal>
);

export default ConfirmWipe;
